import React, { useCallback, useEffect, useState } from "react"
import { useSocketProvider } from "../../../../../Providers/SocketProvider"
import { useRecoilState } from "recoil"
import { currentPatientFileInfoAtom } from "../Atoms/PatientFile"
import { useModalProvider } from "../../../../../Providers/ModalProvider"
import ConfirmationModal from "../../../../../Components/ConfirmationModal/ConfirmationModal"
import { MobergTheme } from "../../../../../Moberg"
import { useEnvironmentVariablesProvider } from "../../../../../Providers/EnvironmentVariablesProvider"
import { useOnMount } from "../../../../../Hooks/useOnMount"


export type CheckRecordingInfoResponse = {
    isAdmitted: boolean
    startTime: number
    endTime: number
    patientIdentifier: string
    mrnError?: string
    error?: string
}

type CheckRecordingInfoProps = {
    dataObjectId: number
    intervalMilliseconds: number
}

export const useCheckRecordingInfo = ({ dataObjectId, intervalMilliseconds }: CheckRecordingInfoProps) => {
    // While the patient is admitted, periodically check for updated information.
    const { getDataQuerySocket } = useSocketProvider()
    const { environmentVariables } = useEnvironmentVariablesProvider()
    const [currentPatientFileInfo, setPatientFileInfo] = useRecoilState(currentPatientFileInfoAtom)
    const { createModal } = useModalProvider()
    const [error, setError] = useState<string>()
    
    const handleSocketResponse = useCallback((socketResponse: CheckRecordingInfoResponse) => {
        if (socketResponse.error) {
            setError(socketResponse.error)
            createModal(<ConfirmationModal
                title={"Server Error"}
                description={"Report this error to your workspace admin: " + socketResponse.error}
                confirmButton={{ text: "OK", theme: MobergTheme.BLUE }}
                showCancelButton={false}
            />, {
                afterClose: () => setError(undefined)
            })
        } else {
            setPatientFileInfo(previous => ({...previous,
                patientIdentifier: socketResponse.patientIdentifier,
                isAdmitted: socketResponse.isAdmitted,
                fileStartDate: new Date(socketResponse.startTime),
                fileEndDate: new Date(socketResponse.endTime),
                mrnError: socketResponse.mrnError
            }))
        }
    }, [createModal, setPatientFileInfo])
    
    // Every "interval" milliseconds, check recording information.
    useEffect(() => {
        if (!currentPatientFileInfo.isAdmitted || error) {
            return
        }

        const socket = getDataQuerySocket()
        const intervalId = setInterval(() => socket.emit("check_recording_info", dataObjectId, environmentVariables.FEATURE_FLAG_ADT_INTEGRATION), intervalMilliseconds)

        return () => {
            clearTimeout(intervalId)
        }
    }, [dataObjectId, getDataQuerySocket, currentPatientFileInfo, error, intervalMilliseconds, environmentVariables.FEATURE_FLAG_ADT_INTEGRATION])

    // Attach socket listeners to receive responses from the server.
    useEffect(() => {
        const socket = getDataQuerySocket()
        socket.on("check_recording_info", handleSocketResponse)

        return () => {
            socket.off("check_recording_info", handleSocketResponse)
        }
    }, [getDataQuerySocket, handleSocketResponse])

    // Check recording info once on initialization.
    useOnMount(() => {
        const socket = getDataQuerySocket()
        socket.emit("check_recording_info", dataObjectId, environmentVariables.FEATURE_FLAG_ADT_INTEGRATION)
    })
}
